import React from 'react';
import { Link } from 'react-router-dom';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Mail, ArrowLeft, GraduationCap, Briefcase, Target, Heart, Lightbulb } from 'lucide-react';

const Founders = () => {
  return (
    <div className="min-h-screen bg-gradient-to-b from-gray-50 to-white">
      {/* Header */}
      <div className="bg-gradient-to-r from-emerald-600 to-teal-600 text-white py-16">
        <div className="max-w-6xl mx-auto px-4">
          <Link to="/about" className="inline-flex items-center text-white/80 hover:text-white mb-6">
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to About
          </Link>
          <h1 className="text-4xl sm:text-5xl font-bold mb-4">Meet Our Founder</h1>
          <p className="text-lg max-w-2xl opacity-90">
            The passionate leader behind Wayo, dedicated to transforming the college application experience for international students.
          </p>
        </div>
      </div>

      {/* Founder Section */}
      <div className="max-w-5xl mx-auto px-4 py-16">
        <Card className="overflow-hidden">
          <div className="md:flex">
            {/* Photo Section */}
            <div className="md:w-1/3 bg-gradient-to-br from-emerald-500 to-teal-600 p-8 flex flex-col items-center justify-center">
              <img
                src="https://customer-assets.emergentagent.com/job_043b277f-ed40-483f-958b-838443341ffd/artifacts/k5crblm3_BeautyEditor_20260125_165204_0.jpeg"
                alt="Hamza Ali"
                className="w-48 h-48 rounded-full object-cover border-4 border-white shadow-xl mb-6"
              />
              <h2 className="text-2xl font-bold text-white text-center">Hamza Ali</h2>
              <p className="text-emerald-100 text-center">Founder & CEO</p>
              
              {/* Social Links */}
              <div className="flex gap-4 mt-6">
                <a href="mailto:hamza@wayo.com"
                   className="w-10 h-10 bg-white/20 rounded-full flex items-center justify-center hover:bg-white/30 transition">
                  <Mail className="w-5 h-5 text-white" />
                </a>
              </div>
            </div>

            {/* Bio Section */}
            <CardContent className="md:w-2/3 p-8">
              <div className="prose max-w-none space-y-6">
                <div>
                  <h3 className="text-xl font-semibold mb-4 flex items-center gap-2">
                    <GraduationCap className="w-5 h-5 text-emerald-600" />
                    My Journey
                  </h3>
                  <p className="text-gray-600 mb-4">
                    I moved to the United States in 2019 as an international student with big goals and very little clarity about how the process really worked.
                  </p>
                  <p className="text-gray-600 mb-4">
                    Like many students, I had to figure things out on my own. College applications, financial documentation, the F1 visa process, and understanding what it truly takes to stand out were all things I made sense of through experience rather than guidance. I remember how confusing it felt. The information online was overwhelming, consultants were expensive, and not all advice felt transparent or trustworthy.
                  </p>
                  <p className="text-gray-600">
                    That experience stayed with me.
                  </p>
                </div>

                <div>
                  <h3 className="text-xl font-semibold mb-4 flex items-center gap-2">
                    <Briefcase className="w-5 h-5 text-emerald-600" />
                    Background & Experience
                  </h3>
                  <p className="text-gray-600 mb-4">
                    After earning my BSc in Economics, I worked across retail operations and technology, finance, and business analytics. Through these roles, I developed strong strategic thinking skills and learned how to guide teams through complex challenges with clear, structured approaches.
                  </p>
                </div>

                <div>
                  <h3 className="text-xl font-semibold mb-4 flex items-center gap-2">
                    <Lightbulb className="w-5 h-5 text-emerald-600" />
                    The Spark
                  </h3>
                  <p className="text-gray-600 mb-4">
                    At the same time, I developed a strong understanding for artificial intelligence. I began learning machine learning development and exploring how technology can solve real world problems. As I studied AI, I realized that I can use it to better the college admissions process for international students which is filled with inefficiencies, information gaps, and unnecessary stress. Students often lack structured guidance and honest insight.
                  </p>
                  <p className="text-gray-600 mb-4">
                    That realization is what motivated me to build this platform.
                  </p>
                  <p className="text-gray-600 mb-4">
                    I love brand building and creating value for the third of a price from the market.
                  </p>
                  <p className="text-gray-700 font-semibold italic">
                    It gives me that KICK
                  </p>
                </div>
              </div>
            </CardContent>
          </div>
        </Card>

        {/* Vision Section */}
        <div className="mt-12 grid md:grid-cols-2 gap-8">
          <Card className="p-6">
            <div className="flex items-start gap-4">
              <div className="w-12 h-12 bg-emerald-100 rounded-full flex items-center justify-center flex-shrink-0">
                <Target className="w-6 h-6 text-emerald-600" />
              </div>
              <div>
                <h3 className="text-lg font-semibold mb-2">My Goal</h3>
                <p className="text-gray-600">
                  My goal is not just to provide advice. I want to build a business rooted in integrity and transparency, where students receive honest guidance, realistic expectations, and strategic direction. No false promises. No shortcuts. Just clarity, structure, and support.
                </p>
              </div>
            </div>
          </Card>

          <Card className="p-6">
            <div className="flex items-start gap-4">
              <div className="w-12 h-12 bg-emerald-100 rounded-full flex items-center justify-center flex-shrink-0">
                <Heart className="w-6 h-6 text-emerald-600" />
              </div>
              <div>
                <h3 className="text-lg font-semibold mb-2">Why This Matters</h3>
                <p className="text-gray-600">
                  This platform represents the intersection of my journey as an international student, my background in economics and finance, and my passion for AI and innovation.
                </p>
              </div>
            </div>
          </Card>
        </div>

        {/* Personal Message */}
        <Card className="mt-12 bg-gradient-to-r from-emerald-50 to-teal-50 border-emerald-200">
          <CardContent className="p-8">
            <h3 className="text-xl font-semibold mb-4 text-emerald-800">A Message to You</h3>
            <p className="text-gray-700 mb-4">
              I have been in your position. I understand the pressure, the ambition, and the uncertainty that comes with pursuing education in a new country. I built this to make the path forward clearer than it was for me.
            </p>
            <p className="text-emerald-700 font-medium">
              If you are ready to take that next step, I am here to help you approach it with strategy, confidence, and transparency.
            </p>
          </CardContent>
        </Card>
      </div>

      {/* Join Us Section */}
      <div className="bg-gray-50 py-16">
        <div className="max-w-4xl mx-auto px-4 text-center">
          <h2 className="text-3xl font-bold mb-4">Join Our Mission</h2>
          <p className="text-gray-600 mb-8">
            We're always looking for passionate individuals who share our vision of making education more accessible.
          </p>
          <div className="flex gap-4 justify-center">
            <Link to="/careers">
              <Button size="lg" className="bg-gradient-to-r from-emerald-500 to-teal-500 text-white px-8">
                View Open Positions
              </Button>
            </Link>
            <Link to="/contact">
              <Button size="lg" variant="outline" className="px-8">
                Get in Touch
              </Button>
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Founders;
